import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { X, Cookie, Shield, BarChart3, Target, Settings } from 'lucide-react';
import { 
  getCookieConsentStatus, 
  getCookiePreferences, 
  acceptAllCookies, 
  rejectNonEssentialCookies,
  saveCookiePreferences,
  CookiePreferences,
  DEFAULT_COOKIE_PREFERENCES 
} from '@/utils/tracking';

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_COOKIE_PREFERENCES);
  const [showFloatingButton, setShowFloatingButton] = useState(false);

  useEffect(() => {
    const consentStatus = getCookieConsentStatus();
    const storedPreferences = getCookiePreferences();
    setPreferences(storedPreferences);

    if (consentStatus === 'pending') {
      setIsVisible(true);
    } else {
      setShowFloatingButton(true);
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setIsVisible(false);
    setShowPreferences(false);
    setShowFloatingButton(true);
  };

  const handleRejectAll = () => {
    rejectNonEssentialCookies();
    setIsVisible(false);
    setShowPreferences(false);
    setShowFloatingButton(true);
  };

  const handleSavePreferences = () => {
    saveCookiePreferences(preferences, { consentStatus: 'accepted' });
    setIsVisible(false);
    setShowPreferences(false);
    setShowFloatingButton(true);
  };

  const handleCloseBanner = () => {
    setIsVisible(false);
    setShowPreferences(false);
    setShowFloatingButton(true);
  };

  const handlePreferenceChange = (key: keyof CookiePreferences, value: boolean) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  return (
    <>
      {isVisible && (
        <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-black/50 z-50" />

          {/* Banner */}
          <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
            <Card className="max-w-4xl mx-auto shadow-2xl border-primary/20">
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
                <div className="flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg">Gestion des cookies</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseBanner}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>

              <CardContent className="space-y-4">
                {!showPreferences ? (
                  <>
                    <CardDescription className="text-sm leading-relaxed">
                      Nous utilisons des cookies pour améliorer votre expérience sur notre site,
                      analyser notre trafic et personnaliser le contenu. Vous pouvez accepter tous
                      les cookies ou personnaliser vos préférences.
                    </CardDescription>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={handleAcceptAll}
                        className="flex-1"
                      >
                        Accepter tous les cookies
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowPreferences(true)}
                        className="flex-1"
                      >
                        Personnaliser
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={handleRejectAll}
                        className="flex-1"
                      >
                        Rejeter non-essentiels
                      </Button>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      En continuant, vous acceptez notre{' '}
                      <a href="/politique-confidentialite" className="underline">
                        Politique de Confidentialité
                      </a>
                    </p>
                  </>
                ) : (
                  <>
                    <CardDescription>
                      Personnalisez vos préférences de cookies selon vos besoins.
                    </CardDescription>

                    <div className="space-y-4">
                      {/* Cookies nécessaires */}
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Shield className="w-5 h-5 text-green-600" />
                          <div>
                            <h4 className="font-medium">Cookies nécessaires</h4>
                            <p className="text-sm text-muted-foreground">
                              Requis pour le fonctionnement du site
                            </p>
                          </div>
                        </div>
                        <Badge variant="secondary">Obligatoire</Badge>
                      </div>

                      {/* Cookies analytiques */}
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <BarChart3 className="w-5 h-5 text-blue-600" />
                          <div>
                            <h4 className="font-medium">Cookies analytiques</h4>
                            <p className="text-sm text-muted-foreground">
                              Nous aident à comprendre comment vous utilisez le site
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={preferences.analytics}
                          onCheckedChange={(value) => handlePreferenceChange('analytics', value)}
                        />
                      </div>

                      {/* Cookies marketing */}
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Target className="w-5 h-5 text-purple-600" />
                          <div>
                            <h4 className="font-medium">Cookies marketing</h4>
                            <p className="text-sm text-muted-foreground">
                              Utilisés pour personnaliser les publicités
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={preferences.marketing}
                          onCheckedChange={(value) => handlePreferenceChange('marketing', value)}
                        />
                      </div>

                      {/* Cookies fonctionnels */}
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Settings className="w-5 h-5 text-orange-600" />
                          <div>
                            <h4 className="font-medium">Cookies fonctionnels</h4>
                            <p className="text-sm text-muted-foreground">
                              Améliorent les fonctionnalités du site
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={preferences.functional}
                          onCheckedChange={(value) => handlePreferenceChange('functional', value)}
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button onClick={handleSavePreferences} className="flex-1">
                        Sauvegarder les préférences
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowPreferences(false)}
                        className="flex-1"
                      >
                        Retour
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {showFloatingButton && !isVisible && (
        <div className="fixed bottom-20 right-4 z-40">
          <Button onClick={() => {
            setIsVisible(true);
            setShowPreferences(false);
          }}>
            <Cookie className="w-4 h-4 mr-2" /> Gérer les cookies
          </Button>
        </div>
      )}
    </>
  );
};
